import React, { MouseEventHandler } from 'react'
import cx from 'classnames'
import { ConnectableElement } from 'react-dnd'
import { Icon as ManyUIIcon } from '@manychat/manyui'

import { IBlock } from 'common/builder/blocks/blockInterfaces'
import { IButton, IButtonStats } from 'common/builder/buttons/buttonInterfaces'
import { isUrlButton } from 'common/builder/buttons/buttonTypeHelpers'
import FlowTextViewer from 'common/builder/components/misc/FlowTextViewer'
import { BlockType } from 'common/builder/constants/BlockType'
import { ButtonType } from 'common/builder/constants/ButtonType'
import ButtonModel from 'common/builder/models/Button'
import { formatCTR } from 'utils/stats'

import cm from '../Button.module.css'

interface ButtonContentProps {
  readonly: boolean
  button: IButton
  buttonBlock: IBlock
  stats: IButtonStats & { ctr: number }
  isSimple: boolean
  disabled: boolean
  dragHolder: (content: ConnectableElement) => ConnectableElement
  hasTarget: boolean
  errors: Array<{ prop: string }>
  showErrors: boolean
  btnRefClassName: string
  onOpenNode: MouseEventHandler<SVGElement>
  dirty: boolean
}

export const ButtonContent = ({
  readonly,
  button,
  buttonBlock,
  stats,
  isSimple,
  disabled,
  dragHolder,
  hasTarget,
  errors,
  showErrors,
  btnRefClassName,
  onOpenNode,
  dirty,
}: ButtonContentProps) => {
  const handleRefClick: MouseEventHandler<SVGElement> = (event) => {
    // Stopping event to prevent button selection
    event.stopPropagation()
    onOpenNode(event)
  }

  const buttonIsNotDraggable = [
    BlockType.ONE_TIME_NOTIFY_REQUEST,
    BlockType.MESSAGE_TEMPLATE,
  ].includes(buttonBlock?.type)
  const refError = errors.find((err) => ['target', 'url', 'phone'].includes(err.prop))
  const showRefError = refError && (showErrors || dirty)
  const IconNext = hasTarget ? ManyUIIcon.ChevronRight : ManyUIIcon.CufNoIcon

  const isHasContent = Boolean(
    [ButtonType.CONTENT, ButtonType.BUY].includes(button.type) &&
      'targetId' in button &&
      button.targetId,
  )

  const isNodeWithTargetType = [
    ButtonType.CONTENT,
    ButtonType.BUY,
    ButtonType.WA_LIST_MESSAGE_BUTTON,
  ].includes(button.type)

  return (
    <>
      <FlowTextViewer
        value={ButtonModel.getCaption(button, '...')}
        className={`text-ellipsis ${cm.caption}`}
      />
      {Boolean(
        (readonly && button.type !== ButtonType.SHARE && button.type !== ButtonType.CALL) ||
          (buttonBlock.type === BlockType.CARD && !buttonBlock.isCardHidden),
      ) && (
        <span className={cx(cm.ctr)}>
          {/* we show CTR in ButtonStatsViewer for simple builder */}
          {Boolean(!stats.ctrDisabled && !isSimple) && `CTR ${formatCTR(stats.ctr)}`}
        </span>
      )}
      {Boolean(!readonly && !buttonIsNotDraggable) &&
        dragHolder(
          <span>
            <ManyUIIcon.ArrowDrag size={24} className={cm.dragHolder} />
          </span>,
        )}
      {readonly ? (
        <>
          {button.type === ButtonType.BUY && (
            <ManyUIIcon.OrphanBuyButton className={cm.buyButtonIcon} />
          )}
          {isHasContent && (
            <ManyUIIcon.ChevronRight
              onClick={handleRefClick}
              className={cx(cm.btnRef, { [cm.disabled]: disabled })}
            />
          )}
        </>
      ) : (
        <>
          {button.type === ButtonType.BUY && (
            <ManyUIIcon.OrphanBuyButton className={cx('m-r-xxs', cm.buyButtonIcon)} />
          )}
          {Boolean(!button.type || isNodeWithTargetType) && (
            <IconNext
              className={cx(cm.btnRef, btnRefClassName, {
                [cm.disabled]: refError || (!hasTarget && isNodeWithTargetType),
                [cm.invalid]: showRefError,
              })}
              onClick={refError || !hasTarget ? () => {} : handleRefClick}
            />
          )}
        </>
      )}
      {button.type === ButtonType.CALL && (
        <ManyUIIcon.OrphanCall className={cx(cm.btnRef, { [cm.invalid]: showRefError })} />
      )}
      {button.type === ButtonType.SHARE && (
        <ManyUIIcon.Share
          size={16}
          className={cx('p-a-xxs', cm.btnRef, { [cm.invalid]: showRefError })}
        />
      )}
      {isUrlButton(button) && (
        <ManyUIIcon.GtRef className={cx(cm.btnRef, { [cm.invalid]: showRefError })} />
      )}
      {button.type === ButtonType.FLOW && (
        <ManyUIIcon.MessageOpen className={cx(cm.btnRef, { [cm.invalid]: showRefError })} />
      )}
    </>
  )
}
